
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import { UploadCloud, CheckCircle, Search, LayoutGrid, List as ListIcon, AlertCircle, Loader2, Star, LayoutTemplate, Download, Calendar, Filter, Archive, Lock, ArrowRight, Camera, Trash2, CheckSquare, Square, RefreshCw, X, ChevronRight, ShieldCheck, Brain, Sparkles } from 'lucide-react';
import { parseClientZip, exportClientsToZip } from './utils/zipHandler';
import { db } from './services/db';
import { ClientEntity, ViewMode } from './types';
import { ClientModal } from './components/ClientModal';
import { SitePreview } from './components/SitePreview';
import { SiteEditor } from './components/SiteEditor';
import { ImportacaoEmMassa } from './components/ImportacaoEmMassa';
import { CriacaoEmMassa } from './components/CriacaoEmMassa';
import { ClientLogin } from './components/ClientLogin';
import { ClientDashboard } from './components/ClientDashboard';
import { ManualClientModal } from './components/ManualClientModal';
import { CustomerPortal } from './components/CustomerPortal';
import { PublicInvoiceView } from './components/PublicInvoiceView';
import { injectClientLoginButton } from './utils/loginInjector';
import { extractBrandingFromHtml } from './utils/brandExtractor';
import { AuthLogin } from './components/auth/AuthLogin';
import { TeamManager } from './components/admin/TeamManager';
import { IaControlView } from './components/admin/IaControlView';
import { VendedorDashboard } from './components/dashboards/VendedorDashboard';
import { auth, tokenDoPortal } from './lib/api';
import { ErroApiNyroh } from '@nyroh/contracts';
import { urlPublicaDoSite } from './lib/site-publico';

// --- Placeholder for Site View (When URL is #/site/:slug or Custom Domain) ---
const PublicSiteViewer: React.FC<{ slug?: string; domain?: string }> = ({ slug, domain }) => {
  const [client, setClient] = useState<ClientEntity | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadSite = async () => {
      let data;
      if (domain) {
        data = await db.getClientByDomain(domain);
      } else if (slug) {
        data = await db.getClientBySlug(slug);
      }
      setClient(data || null);
      setLoading(false);
    };
    loadSite();
  }, [slug, domain]);

  const safeHtml = useMemo(() => {
    if (!client) return null;
    const rawPhone = client.data.phoneNumber.replace(/[^0-9]/g, '');
    const waLink = rawPhone ? `https://wa.me/55${rawPhone}?text=Ol%C3%A1!%20Vim%20pelo%20site.` : '#';
    const brand = extractBrandingFromHtml(client.htmlContent, client.data);

    const fixScript = `
      <script>
        document.addEventListener('DOMContentLoaded', function() {
          var clientWa = "${waLink}";
          document.body.addEventListener('click', function(e) {
            var target = e.target.closest('a');
            if (!target) return;
            var href = target.getAttribute('href');
            if (href && (href.includes('#/login') || href.includes('/login'))) {
               e.preventDefault(); e.stopPropagation();
               var targetHash = href.startsWith('#') ? href : ('#' + href.replace(/^[^#]*#/, ''));
               try { if (window.top) { window.top.location.hash = targetHash; } } catch(err) {}
               try { if (window.parent) { window.parent.postMessage({ type: 'NAVIGATE', hash: targetHash }, '*'); window.parent.location.hash = targetHash; } } catch(err) {}
               window.location.hash = targetHash;
               return;
            }
            if (!href || href === '#' || href === 'javascript:void(0)' || href === '') {
               e.preventDefault(); e.stopPropagation();
               if (clientWa && clientWa !== '#') { window.open(clientWa, '_blank'); }
               return;
            }
            if (href.startsWith('tel:') || href.startsWith('mailto:') || href.startsWith('http') || href.startsWith('https') || href.startsWith('//') || href.includes('wa.me') || href.includes('whatsapp') || href.includes('instagram') || href.includes('facebook')) {
               e.preventDefault(); e.stopPropagation(); window.open(href, '_blank');
            }
          }, true);
        });
      </script>
    `;
    let html = client.htmlContent;
    // Injeta o botão mágico de login da Área do Cliente com a cor da marca
    html = injectClientLoginButton(html, client.slug, client.data?.name, brand.primaryColor);

    if (html.includes('</body>')) { return html.replace('</body>', fixScript + '</body>'); }
    return html + fixScript;
  }, [client]);

  if (loading) return <div className="h-screen flex items-center justify-center bg-white"><Loader2 className="animate-spin text-indigo-600" size={40} /></div>;
  if (!client || !safeHtml) return <div className="h-screen flex items-center justify-center bg-white text-slate-500">Site Not Found (404)</div>;

  return (
    <div className="w-full h-screen bg-white">
      <iframe srcDoc={safeHtml} title="Client Site" className="w-full h-full border-0" style={{ display: 'block' }} sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-popups-to-escape-sandbox allow-top-navigation allow-top-navigation-by-user-activation" />
    </div>
  );
};

const DeleteConfirmationModal: React.FC<{ isOpen: boolean; count: number; onConfirm: () => void; onCancel: () => void; isDeleting: boolean; }> = ({ isOpen, count, onConfirm, onCancel, isDeleting }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl p-6 text-center">
        <div className="w-16 h-16 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4"><Trash2 size={32} /></div>
        <h3 className="text-xl font-bold text-slate-900 mb-2">Confirmar Exclusão</h3>
        <p className="text-slate-500 mb-6">Você tem certeza que deseja excluir <b>{count}</b> cliente(s)? <br/>Essa ação não pode ser desfeita.</p>
        <div className="flex gap-3 justify-center">
          <button onClick={onCancel} disabled={isDeleting} className="px-5 py-2.5 rounded-lg border border-slate-200 text-slate-600 font-medium hover:bg-slate-50 transition-colors">Cancelar</button>
          <button onClick={onConfirm} disabled={isDeleting} className="px-5 py-2.5 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 transition-colors flex items-center gap-2 shadow-lg shadow-red-200">{isDeleting ? <Loader2 className="animate-spin" size={18} /> : null}{isDeleting ? 'Excluindo...' : 'Sim, Excluir'}</button>
        </div>
      </div>
    </div>
  );
};

import { AgencyDashboard } from './components/AgencyDashboard';
import { ShowcaseDashboard } from './components/showcase/ShowcaseDashboard';
import { PublicShowcase } from './components/showcase/viewer/PublicShowcase';
import { NyrohLanding } from './components/NyrohLanding';
import { AdminFinanceBoard } from './components/AdminFinanceBoard';
import { Proposta } from './components/Proposta';
import { PropostaBuilder } from './components/PropostaBuilder';
import { DocumentsDashboard } from './components/DocumentsDashboard';
import { IntegrationsDashboard } from './components/IntegrationsDashboard';

function App() {
  const [customDomainHost, setCustomDomainHost] = useState<string | null>(null);
  const [isCheckingDomain, setIsCheckingDomain] = useState(true);
  
  // === SESSÃO DO OPERADOR (Better Auth) ===
  //
  // Antes eram DUAS idas ao servidor: uma para a sessão, outra para descobrir o
  // cargo em `user_roles`. Essa segunda consulta é a origem do BUG-1 — o cargo
  // vivia numa tabela paralela que podia não existir para um usuário recém
  // criado, e a tela não sabia diferenciar "sem cargo" de "cargo carregando".
  //
  // Agora o cargo faz parte do próprio usuário da sessão: chega junto, ou não
  // chega sessão nenhuma.
  const { data: sessaoAuth, isPending: isSessionLoading } = auth.useSession();
  const session = (sessaoAuth ?? null) as { user?: { id: string; cargo?: string } } | null;
  /**
   * O id do operador logado, ou `null`.
   *
   * Existe para ser DEPENDENCIA de efeito. `session` e um objeto: se a
   * referencia mudar a cada render — e nao ha garantia de que nao mude — um
   * efeito que dependa dele dispara em laco, e cada volta e uma requisicao de
   * listagem. Uma string primitiva nao tem esse risco.
   */
  const idOperador = session?.user?.id ?? null;
  const dbRole = session?.user?.cargo ?? null;
  // ========================

  useEffect(() => {
    const hostname = window.location.hostname;
    
    // Define main domains that should NOT be treated as a client custom domain
    const isMainDomain = 
      hostname.includes('localhost') || 
      hostname.includes('vercel.app') || 
      hostname.includes('run.app') ||
      hostname.includes('google.com') ||
      hostname.includes('aistudio.google.com') ||
      hostname.includes('nyroh.com.br');

    if (!isMainDomain) {
      // It's a custom domain, strip www. just in case
      const cleanDomain = hostname.toLowerCase().replace(/^www\./, '');
      setCustomDomainHost(cleanDomain);
    }
    
    setIsCheckingDomain(false);
  }, []);

  const [isAuthenticated, setIsAuthenticated] = useState(false); // Legacy auth state, keeping for safety, but we use session now
  const [userRole, setUserRole] = useState<'admin' | 'agency' | 'material' | null>(null);
  const [loginError, setLoginError] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('upload');
  const [clients, setClients] = useState<ClientEntity[]>([]);
  /**
   * Por que a listagem falhou, quando falhou.
   *
   * `loadClients` nao tinha tratamento de erro: qualquer falha na chamada
   * — sessao expirada, CORS, a API fora do ar, resposta fora do contrato —
   * virava uma promise rejeitada em silencio. `setClients` nunca rodava, a
   * tela caia no estado vazio, e "Nenhum projeto encontrado" ficava
   * INDISTINGUIVEL de "a busca nao achou nada".
   *
   * Aconteceu de verdade: um cliente que estava no banco sumiu do painel e nao
   * havia como saber se tinha sido apagado, filtrado ou se a chamada morreu.
   */
  const [erroListagem, setErroListagem] = useState<string | null>(null);
  const [pendingClients, setPendingClients] = useState<ClientEntity[]>([]);
  const [selectedClient, setSelectedClient] = useState<ClientEntity | null>(null);
  const [previewClient, setPreviewClient] = useState<ClientEntity | null>(null);
  const [editingClient, setEditingClient] = useState<ClientEntity | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [totalClientsCount, setTotalClientsCount] = useState<number>(0);
  const [isExportingJson, setIsExportingJson] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isGeneratingCovers, setIsGeneratingCovers] = useState(false);
  const abortCoverGeneration = useRef(false);
  const [regeneratingId, setRegeneratingId] = useState<string | null>(null);
  const [coverImages, setCoverImages] = useState<Record<string, string>>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isManualModalOpen, setIsManualModalOpen] = useState(false);
  const [idsToDelete, setIdsToDelete] = useState<string[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // DATE FILTER STATES
  const [filterDate, setFilterDate] = useState<'all' | 'today' | 'yesterday' | 'week' | 'custom'>('today');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const [filterNiche, setFilterNiche] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterMinRating, setFilterMinRating] = useState<number>(0);
  const [layoutView, setLayoutView] = useState<'grid' | 'list'>('grid');
  
  const getInitialSlug = () => {
      const hash = window.location.hash;
      if (hash.startsWith('#/site/')) {
         const parts = hash.split('/');
         if (parts[1] === 'site') return parts[2];
      }
      return null;
  };

  const [publicSlug, setPublicSlug] = useState<string | null>(getInitialSlug());
  const [isEditMode, setIsEditMode] = useState(() => window.location.hash.includes('/edit'));
  // `startsWith` e nao `===`: o link de acesso que o painel entrega ao cliente e
  // `#/login/<slug>`, e na carga FRIA (colar a URL, abrir num navegador novo)
  // esta comparacao dava falso — o app caia na landing. So depois de uma troca de
  // hash o `handleHashChange`, que ja usava `startsWith`, acertava. Quem clicava
  // no link recebido chegava na pagina de vendas, nao no login.
  const [isClientLogin, setIsClientLogin] = useState(
    () => window.location.hash === '#/login' || window.location.hash.startsWith('#/login/'),
  );
  const [isClientPanel, setIsClientPanel] = useState(() => window.location.hash === '#/painel');
  const [isCoreAdmin, setIsCoreAdmin] = useState(() => window.location.hash === '#/core');
  const [isLanding, setIsLanding] = useState(() => window.location.hash === '' || window.location.hash === '#/' || window.location.hash === '#');
  /**
   * Restaurando a sessao do portal ao carregar a pagina.
   *
   * O cliente entrava, apertava F5 e era jogado para o login — o token continuava
   * valido em `sessionStorage` e no banco, mas `loggedInClient` so vivia em
   * estado do React. Nada no painel chamava `portal.sessao`, que existe no
   * contrato desde o inicio justamente para esta pergunta.
   */
  const [restaurandoPortal, setRestaurandoPortal] = useState(() => tokenDoPortal() !== null);
  const [propostaSlug, setPropostaSlug] = useState<string | null>(() => {
    return window.location.hash.startsWith('#/proposta/') ? window.location.hash.replace('#/proposta/', '') : null;
  });
  const [publicShowcaseSlug, setPublicShowcaseSlug] = useState<string | null>(() => {
    return window.location.hash.startsWith('#/showcase/') ? window.location.hash.replace('#/showcase/', '') : null;
  });
  const [publicInvoiceId, setPublicInvoiceId] = useState<string | null>(() => {
    const hash = window.location.hash;
    if (hash.startsWith('#/fatura/')) return hash.replace('#/fatura/', '').split('?')[0];
    if (hash.startsWith('#/cobranca/')) return hash.replace('#/cobranca/', '').split('?')[0];
    if (hash.startsWith('#/invoice/')) return hash.replace('#/invoice/', '').split('?')[0];
    return null;
  });
  const [customerPortalSlug, setCustomerPortalSlug] = useState<string | null>(() => {
    const hash = window.location.hash;
    if (hash.startsWith('#/portal/')) return hash.replace('#/portal/', '');
    if (hash === '#/portal') return '';
    if (hash.startsWith('#/atendimento/')) return hash.replace('#/atendimento/', '');
    if (hash === '#/atendimento') return '';
    return null;
  });
  const [loggedInClient, setLoggedInClient] = useState<ClientEntity | null>(null);

  useEffect(() => {
    if (!restaurandoPortal) return;
    let cancelado = false;

    void (async () => {
      try {
        const cliente = await db.restoreClientSession();
        if (!cancelado && cliente) setLoggedInClient(cliente);
      } catch (e) {
        // Token expirado ou API fora. Nao e erro para o usuario: ele so cai no
        // login, que e o que ja acontecia. Mas nao passa em silencio.
        console.warn('[app] nao consegui restaurar a sessao do portal:', e);
      } finally {
        if (!cancelado) setRestaurandoPortal(false);
      }
    })();

    return () => {
      cancelado = true;
    };
  }, [restaurandoPortal]);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      
      setIsClientLogin(hash === '#/login' || hash.startsWith('#/login/'));
      setIsClientPanel(hash === '#/painel');
      setIsCoreAdmin(hash === '#/core');
      setIsLanding(hash === '' || hash === '#/' || hash === '#');

      if (hash.startsWith('#/fatura/') || hash.startsWith('#/cobranca/') || hash.startsWith('#/invoice/')) {
        const invId = hash.replace('#/fatura/', '').replace('#/cobranca/', '').replace('#/invoice/', '').split('?')[0];
        setPublicInvoiceId(invId);
        return;
      } else {
        setPublicInvoiceId(null);
      }

      if (hash.startsWith('#/portal/') || hash === '#/portal' || hash.startsWith('#/atendimento/') || hash === '#/atendimento') {
        const cleanSlug = hash.replace('#/portal/', '').replace('#/portal', '').replace('#/atendimento/', '').replace('#/atendimento', '');
        setCustomerPortalSlug(cleanSlug || '');
        return;
      } else {
        setCustomerPortalSlug(null);
      }
      
      if (hash.startsWith('#/showcase/')) {
        setPublicShowcaseSlug(hash.replace('#/showcase/', ''));
        return;
      } else {
        setPublicShowcaseSlug(null);
      }

      if (hash.startsWith('#/proposta/')) {
        setPropostaSlug(hash.replace('#/proposta/', ''));
        return;
      } else {
        setPropostaSlug(null);
      }

      if (hash.includes('/edit')) {
         const parts = hash.split('/');
         if (parts[1] === 'site' && parts[3] === 'edit') {
            setPublicSlug(parts[2]); setIsEditMode(true); return;
         }
      }
      if (hash.startsWith('#/site/')) {
        const slug = hash.replace('#/site/', '');
        setPublicSlug(slug); setIsEditMode(false);
      } else {
        setPublicSlug(null); setIsEditMode(false);
      }
    };
    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  /**
   * Carrega a lista quando ha SESSAO — nao quando `isAuthenticated` e true.
   *
   * `isAuthenticated` e o resto do login antigo, aquele que comparava senha
   * dentro do navegador (ver o bloco removido mais abaixo). Ele nasce `false` e
   * NUNCA e posto em `true` em lugar nenhum do arquivo: as unicas atribuicoes
   * restantes sao os tres `setIsAuthenticated(false)` do logout. O comentario
   * na declaracao dizia "legacy auth state, keeping for safety" — mas a
   * condicao deste efeito continuou dependendo dele.
   *
   * Consequencia: em toda carga limpa da pagina, `loadClients` simplesmente NAO
   * RODAVA. Nenhuma requisicao para `/v1/clientes` — a aba Network fica vazia —
   * e a tela mostra "Nenhum projeto encontrado" com o banco cheio.
   *
   * Parecia funcionar porque `loadClients()` tambem e chamado direto depois de
   * aprovar um upload, criar cliente manual e excluir. Quem acabava de importar
   * via a lista; quem recarregava a pagina, nao.
   *
   * A fonte da verdade e a sessao do Better Auth, que e o que o resto da tela
   * ja usa para decidir se mostra o painel ou a tela de login.
   */
  useEffect(() => {
    if (isSessionLoading) return;
    if ((idOperador && !publicSlug) || publicShowcaseSlug) {
      loadClients(debouncedSearchTerm, filterDate, customStartDate, customEndDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idOperador, isSessionLoading, publicSlug, publicShowcaseSlug, debouncedSearchTerm, filterDate, customStartDate, customEndDate]);

  // Mesmo motivo do efeito acima: preso em `isAuthenticated`, a contagem da
  // tela de integracoes nunca era buscada e ficava eternamente em zero.
  useEffect(() => {
    if (viewMode === 'export_json' && idOperador) {
      db.getTotalClientsCount()
        .then((count) => setTotalClientsCount(count))
        .catch((e) => console.error('[painel] falha ao contar clientes', e));
    }
  }, [viewMode, idOperador]);

  const loadClients = async (search: string = debouncedSearchTerm, dateFilterVal: string = filterDate, cStart: string = customStartDate, cEnd: string = customEndDate) => {
    const limit = 100000;
    try {
      const data = await db.getClients(search, limit, dateFilterVal, cStart, cEnd);
      setClients(data);
      setErroListagem(null);
      setSelectedIds(new Set());
    } catch (e) {
      // O estado anterior NAO e limpo de proposito: apagar a lista da tela por
      // causa de uma falha de rede faz parecer que os dados sumiram.
      console.error('[painel] falha ao listar clientes', e);
      setErroListagem(
        e instanceof ErroApiNyroh
          ? `${e.codigo}: ${e.message}`
          : e instanceof Error
            ? e.message
            : 'não consegui falar com a API.',
      );
    }
  };

  /**
   * ============================================================================
   * REMOVIDO — o login que comparava a senha dentro do navegador
   * ============================================================================
   *
   * Aqui existia um `handleLogin` que comparava `passwordInput` com TRES senhas
   * escritas em texto puro neste arquivo e, batendo, atribuia o cargo:
   *
   *     if (passwordInput === '<a senha de admin>')      setUserRole('admin')
   *     else if (passwordInput === '<a senha da agencia>') setUserRole('agency')
   *     else if (passwordInput === '<a senha de material>') setUserRole('material')
   *
   * Tres problemas, cada um suficiente sozinho:
   *
   * 1. A comparacao rodava no NAVEGADOR. Quem abrisse o DevTools via as tres
   *    senhas e o cargo que cada uma dava. Autorizacao decidida no cliente nao e
   *    autorizacao — e a falha que o diagnostico chama de "senha comparada no
   *    bundle" (secao 05).
   * 2. A primeira senha era a MESMA string usada como chave da Evolution API em
   *    `nyroh-leads`. Uma senha reaproveitada entre o painel e a instancia de
   *    WhatsApp da operacao.
   * 3. A funcao ja nao era chamada por JSX nenhum — o login de verdade e o
   *    `components/auth/AuthLogin.tsx`, contra `POST /api/auth/sign-in/email`.
   *    Mas continuava no arquivo versionado e no sourcemap publicado, que e
   *    onde a senha estava legivel em producao.
   *
   * Apagar isto NAO invalida a senha. Ela precisa ser rotacionada no painel da
   * Evolution API e onde mais tiver sido reusada — ver docs/GIT.md, secao 7.
   */

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setIsLoading(true);
      try {
        const newClients = await parseClientZip(e.target.files[0]);
        setPendingClients(newClients);
      } catch (error) {
        toast.error("Erro ao ler o ZIP. Verifique a estrutura.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const approveAll = async () => {
    setIsLoading(true);
    try {
      const result = await db.saveClients(pendingClients);
      let msg = `${result.saved} sites foram publicados com sucesso!`;
      if (result.skipped > 0) {
        msg += `\n\n${result.skipped} sites foram ignorados automaticamente pois já existem no banco.`;
      }
      toast.success(msg);
      setPendingClients([]);
      await loadClients();
      setViewMode('dashboard');
    } catch (err: any) {
      toast.error(err.message || 'Erro crítico no deploy.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrintReport = () => window.print();

  const allCategories = useMemo(() => {
    const cats = new Set<string>();
    clients.forEach(c => (c.data.categories || []).forEach(cat => cats.add(cat)));
    return Array.from(cats).sort();
  }, [clients]);

  const allStatuses = useMemo(() => {
    const statuses = new Set<string>();
    clients.forEach(c => c.data.businessStatus && statuses.add(c.data.businessStatus));
    return Array.from(statuses).sort();
  }, [clients]);

  const filteredClients = useMemo(() => {
    return clients.filter(c => {
      const matchesSearch = c.data.name?.toLowerCase().includes(searchTerm.toLowerCase()) || (c.data.categories || []).some(cat => cat.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesNiche = filterNiche === 'all' ? true : (c.data.categories || []).includes(filterNiche);
      const matchesStatus = filterStatus === 'all' ? true : c.data.businessStatus === filterStatus;
      const matchesRating = c.data.rating >= filterMinRating;
      
      return matchesSearch && matchesNiche && matchesRating && matchesStatus;
    });
  }, [clients, searchTerm, filterNiche, filterMinRating, filterStatus]);

  const toggleSelection = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) newSet.delete(id); else newSet.add(id);
    setSelectedIds(newSet);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredClients.length && filteredClients.length > 0) setSelectedIds(new Set());
    else { const newSet = new Set<string>(); filteredClients.forEach(c => newSet.add(c.id)); setSelectedIds(newSet); }
  };

  const handleDeleteRequest = (ids: string[]) => { setIdsToDelete(ids); setDeleteModalOpen(true); };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try { await db.deleteClients(idsToDelete); await loadClients(); setDeleteModalOpen(false); setIdsToDelete([]); } 
    catch (error) { toast.error("Erro ao excluir clientes."); } 
    finally { setIsDeleting(false); }
  };

  const handleClientClick = async (client: ClientEntity) => {
    setSelectedClient(client);
    if (!client.htmlContent) {
      try {
        const full = await db.getClientById(client.id);
        if (full) setSelectedClient(full);
      } catch (err) {
        console.error("Erro ao carregar detalhes do cliente:", err);
      }
    }
  };

  const captureScreenshot = async (client: ClientEntity): Promise<string | null> => {
     try {
       const html2canvas = (await import('html2canvas')).default;
       const container = document.createElement('div');
       container.style.position = 'fixed'; container.style.left = '-9999px'; container.style.top = '0'; container.style.width = '1280px'; container.style.height = '800px'; container.style.backgroundColor = '#ffffff'; container.style.zIndex = '-100';
       document.body.appendChild(container);
       const iframe = document.createElement('iframe');
       iframe.style.width = '1280px'; iframe.style.height = '720px'; iframe.style.border = 'none'; iframe.style.backgroundColor = 'white';
       container.appendChild(iframe);
       const doc = iframe.contentWindow?.document;
       if (doc) { 
         // Injeta CSS para desativar animações e transições e evitar textos "fantasmas"
         let htmlToRender = client.htmlContent;
         const noAnimStyle = '<style>*, *::before, *::after { animation: none !important; transition: none !important; scroll-behavior: auto !important; }</style>';
         if (htmlToRender.includes('</head>')) {
           htmlToRender = htmlToRender.replace('</head>', noAnimStyle + '</head>');
         } else {
           htmlToRender = noAnimStyle + htmlToRender;
         }
         doc.open(); doc.write(htmlToRender); doc.close(); 
       }
       // Aumentamos o delay para 2.5 segundos para garantir carregamento de fontes e imagens
       await new Promise(r => setTimeout(r, 2500));
       let dataUrl: string | null = null;
       if (iframe.contentDocument && iframe.contentDocument.body) {
           const canvas = await html2canvas(iframe.contentDocument.body, { 
             width: 1280, 
             height: 720, 
             windowWidth: 1280,
             windowHeight: 720,
             useCORS: true, 
             logging: false,
             scale: 1,
             backgroundColor: '#ffffff'
           });
           dataUrl = canvas.toDataURL('image/jpeg', 0.85);
           canvas.width = 0; // Limpar memória do canvas manualmente
           canvas.height = 0;
       }
       
       // Clean up explicitly
       iframe.src = 'about:blank';
       document.body.removeChild(container);
       return dataUrl;
     } catch (e) { 
       console.error("Erro na captura de tela:", e);
       // Ensure cleanup on error
       const el = document.body.lastChild;
       if (el && (el as HTMLElement).style.zIndex === '-100') {
           document.body.removeChild(el);
       }
       return null; 
     }
  };

  const generateCovers = async () => {
    setIsGeneratingCovers(true);
    
    // Consider selected items if any exist, otherwise fallback to all filtered
    const clientsToConsider = selectedIds.size > 0 
      ? filteredClients.filter(c => selectedIds.has(c.id))
      : filteredClients;
      
    const clientsToProcess = clientsToConsider.filter(c => !coverImages[c.id]);
    
    if (clientsToProcess.length === 0) {
       toast.info('Todas as capas já foram geradas.');
       setIsGeneratingCovers(false);
       return;
    }
    
    toast.info(`Gerando ${clientsToProcess.length} capas... Isso pode levar vários minutos.`);
    let successCount = 0;
    
    const BATCH_SIZE = 2; // Processa 2 de cada vez para não travar o navegador
    
    abortCoverGeneration.current = false;
    for (let i = 0; i < clientsToProcess.length; i += BATCH_SIZE) {
      if (abortCoverGeneration.current) {
        toast.info(`Geração de capas cancelada. ${successCount} capas geradas.`);
        break;
      }
      const batch = clientsToProcess.slice(i, i + BATCH_SIZE);
      
      const batchResults = await Promise.all(
        batch.map(async (client) => {
          let fullClient = client;
          if (!client.htmlContent) {
            try {
              const fetched = await db.getClientById(client.id);
              if (fetched) fullClient = fetched;
            } catch (err) {
              console.error(`Erro ao buscar conteúdo para capa (${client.slug}):`, err);
            }
          }
          const dataUrl = await captureScreenshot(fullClient);
          return { id: client.id, dataUrl };
        })
      );
      
      setCoverImages(prev => {
        const newCovers = { ...prev };
        batchResults.forEach(result => {
          if (result.dataUrl) {
            newCovers[result.id] = result.dataUrl;
            successCount++;
          }
        });
        return newCovers;
      });
      
      // Libera a thread do navegador
      if (i + BATCH_SIZE < clientsToProcess.length) {
        await new Promise(r => setTimeout(r, 1000));
      }
    }
    
    setIsGeneratingCovers(false);
    if (!abortCoverGeneration.current) {
      toast.success(`Processo concluído. Capas geradas: ${successCount} de ${clientsToProcess.length}.`);
    }
    abortCoverGeneration.current = false;
  };

  const regenerateSingleCover = async (client: ClientEntity, e: React.MouseEvent) => {
    e.stopPropagation(); setRegeneratingId(client.id);
    let fullClient = client;
    if (!client.htmlContent) {
      try {
        const fetched = await db.getClientById(client.id);
        if (fetched) fullClient = fetched;
      } catch (err) {
        console.error(`Erro ao buscar conteúdo para capa (${client.slug}):`, err);
      }
    }
    const dataUrl = await captureScreenshot(fullClient);
    if (dataUrl) setCoverImages(prev => ({ ...prev, [client.id]: dataUrl }));
    setRegeneratingId(null);
  };

  const handleExport = async () => {
    const clientsToExport = selectedIds.size > 0 
      ? filteredClients.filter(c => selectedIds.has(c.id)) 
      : filteredClients;

    if (clientsToExport.length === 0) {
      toast.error('Nenhum cliente disponível para exportar.');
      return;
    }

    setIsExporting(true);
    toast.info(`Preparando ${clientsToExport.length} clientes para exportação. Aguarde...`);
    
    try { 
      // Buscar o HTML completo dos clientes selecionados para o ZIP
      const fullClientsToExport: ClientEntity[] = [];
      const BATCH_SIZE = 10;
      
      for (let i = 0; i < clientsToExport.length; i += BATCH_SIZE) {
        const batch = clientsToExport.slice(i, i + BATCH_SIZE);
        const batchResults = await Promise.all(
          batch.map(async (client) => {
            if (!client.htmlContent) {
              try {
                const fetched = await db.getClientById(client.id);
                if (fetched) return fetched;
              } catch (e) {
                console.error(`Erro ao buscar conteúdo completo do cliente ${client.id}:`, e);
              }
            }
            return client;
          })
        );
        fullClientsToExport.push(...batchResults);
      }
      
      await exportClientsToZip(fullClientsToExport, coverImages); 
      toast.success(`Exportação de ${fullClientsToExport.length} clientes concluída!`);
    } 
    catch (err) { 
      console.error('ERRO FATAL NA GERAÇÃO DO ZIP:', err);
      toast.error('Erro ao gerar o arquivo ZIP.'); 
    }
    finally { setIsExporting(false); }
  };

  const handleExportJson = async () => {
    if (isExportingJson) return;
    setIsExportingJson(true);
    try {
      const allClientsForExport = await db.getAllClientsForExport();
      
      const dataToExport = allClientsForExport.map(client => ({
        id: client.data?.id || client.id,
        nome: client.data?.name || 'Sem nome',
        telefone: client.data?.phoneNumber || '',
        googleMapsLink: client.data?.placeUri || '',
        nicho: (client.data?.categories && (client.data.categories || [])[0]) || '',
        criadoEm: client.data?.createdAt || new Date().toISOString()
      }));

      const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(dataToExport, null, 2));
      const downloadAnchorNode = document.createElement('a');
      downloadAnchorNode.setAttribute("href", dataStr);
      downloadAnchorNode.setAttribute("download", `leads_nyroh_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(downloadAnchorNode);
      downloadAnchorNode.click();
      downloadAnchorNode.remove();
      
      toast.success(`${dataToExport.length} leads exportados com sucesso!`);
    } catch (e) {
      console.error(e);
      toast.error('Erro ao gerar o arquivo JSON completo.');
    } finally {
      setIsExportingJson(false);
    }
  };

  if (publicShowcaseSlug) {
    return <PublicShowcase slug={publicShowcaseSlug} clients={clients} />;
  }

  if (publicInvoiceId) {
    return <PublicInvoiceView invoiceId={publicInvoiceId} />;
  }

  if (customerPortalSlug !== null) {
    return <CustomerPortal slug={customerPortalSlug || undefined} clientEntity={loggedInClient} />;
  }

  // Custom Domain Viewer (No Auth Required, overrides everything)
  if (isCheckingDomain) return <div className="h-screen flex items-center justify-center bg-white"><Loader2 className="animate-spin text-indigo-600" size={40} /></div>;
  if (customDomainHost) return <PublicSiteViewer domain={customDomainHost} />;

  if (isClientLogin) {
    const loginSlug = window.location.hash.startsWith('#/login/') ? window.location.hash.replace('#/login/', '').split('?')[0] : null;
    return <ClientLogin presetSlug={loginSlug} onLogin={(client) => {
      setLoggedInClient(client);
      window.location.hash = '#/painel';
    }} />;
  }

  if (isClientPanel) {
    // Enquanto a sessao esta sendo restaurada, esperar. Redirecionar aqui era o
    // que expulsava do painel quem so tinha apertado F5.
    if (restaurandoPortal) {
      return <div className="h-screen flex items-center justify-center bg-[#FAFAFA]"><Loader2 className="animate-spin text-black" size={32} /></div>;
    }
    if (!loggedInClient) {
      window.location.hash = '#/login';
      return null;
    }
    return <ClientDashboard 
      client={loggedInClient} 
      onLogout={async () => {
        // Encerra a sessao NO SERVIDOR antes de limpar a tela. Sair so apagando
        // o token do navegador deixava a linha viva em `sessoes_portal` ate
        // expirar — quem tivesse copiado o token seguia dentro.
        await db.logoutClient();
        setLoggedInClient(null);
        window.location.hash = '#/login';
      }} 
      onClientUpdate={async () => {
        // Refetch client to update status
        const updatedClient = await db.getClientBySlug(loggedInClient.slug);
        // `precisaTrocarSenha` so vem no login (e o servidor que responde, junto
        // com a sessao). Sem carregar adiante, salvar qualquer coisa no painel
        // apagava o aviso de senha padrao sem ninguem ter trocado nada.
        if (updatedClient) {
          setLoggedInClient({
            ...updatedClient,
            precisaTrocarSenha: loggedInClient.precisaTrocarSenha,
          });
        }
      }}
    />;
  }

  // Defaut Admin Dashboard
  if (isCoreAdmin) {
    if (isSessionLoading) {
      return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="animate-spin text-indigo-600" size={32} /></div>;
    }

    // Se NÃO estiver logado via Supabase E também não tiver as senhas legadas (Agency/Material)
    if (!session && !isAuthenticated) {
        return (
          <div className="relative">
            {/* Botão de fuga para voltar a Landing */}
            <button 
                onClick={() => window.location.hash = ''} 
                className="absolute top-4 left-4 z-50 text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-slate-900 transition-colors flex items-center gap-1 bg-white/80 px-3 py-2 rounded-lg"
            >
              <ChevronRight size={14} className="rotate-180" /> Voltar
            </button>
            <AuthLogin />
          </div>
        );
    }
    
    // Se o usuário logou pelo Supabase, fazemos o roteamento da Nova Era
    if (session) {
      if (dbRole === 'vendedor') {
        return <VendedorDashboard />;
      }
      if (dbRole === 'lider') {
        return <div className="min-h-screen flex items-center justify-center bg-slate-50"><h1 className="text-2xl font-bold text-slate-500">Painel do Líder em construção pela Engenharia...</h1></div>;
      }
      if (dbRole === 'diretora') {
        return <div className="min-h-screen flex items-center justify-center bg-slate-50"><h1 className="text-2xl font-bold text-slate-500">Painel da Diretora em construção pela Engenharia...</h1></div>;
      }
      // Se for admin, gestora, ou ceo, ele passa reto e vê o painel principal (código abaixo)
    }

    // Lógica Legada para Agency e Material (Mantido para não quebrar integrações deles se ainda usarem a senha velha)
    if (!session && isAuthenticated) {
      if (userRole === 'agency') {
        return (
          <AgencyDashboard 
            clients={clients} 
            coverImages={coverImages} 
            onLogout={() => { setIsAuthenticated(false); setUserRole(null); }} 
          />
        );
      }

      if (userRole === 'material') {
        return (
          <ShowcaseDashboard 
            clients={clients} 
            onLogout={() => { setIsAuthenticated(false); setUserRole(null); }} 
          />
        );
      }
    }

    return (
      <div className="min-h-screen bg-[#FAFAFA] font-sans text-gray-900 flex flex-col">
        <DeleteConfirmationModal isOpen={deleteModalOpen} count={idsToDelete.length} onConfirm={confirmDelete} onCancel={() => setDeleteModalOpen(false)} isDeleting={isDeleting} />
        <header className="bg-white border-b border-gray-200 sticky top-0 z-30 no-print">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-black rounded-md flex items-center justify-center">
              <span className="text-white font-bold text-lg leading-none">N</span>
            </div>
            <h1 className="text-lg font-semibold text-gray-900 tracking-tight text-black">NYROH SITES</h1>
          </div>
          <div className="flex items-center gap-6">
            <nav className="flex gap-4">
               <button onClick={() => setViewMode('upload')} className={`text-sm font-medium transition-colors ${viewMode === 'upload' ? 'text-black' : 'text-gray-500 hover:text-gray-900'}`}>Upload</button>
               <button onClick={() => setViewMode('dashboard')} className={`text-sm font-medium transition-colors ${viewMode === 'dashboard' ? 'text-black' : 'text-gray-500 hover:text-gray-900'}`}>Projetos</button>
               <button onClick={() => setViewMode('importacao_massa')} className={`text-sm font-medium transition-colors ${viewMode === 'importacao_massa' ? 'text-black' : 'text-gray-500 hover:text-gray-900'}`}>Importação em massa</button>
               <button onClick={() => setViewMode('criacao_massa')} className={`text-sm font-medium transition-colors flex items-center gap-1 ${viewMode === 'criacao_massa' ? 'text-indigo-600 font-bold' : 'text-gray-500 hover:text-indigo-600'}`}>
                 <Sparkles size={16} /> Criar em massa
               </button>
               <button onClick={() => setViewMode('finance')} className={`text-sm font-medium transition-colors ${viewMode === 'finance' ? 'text-black' : 'text-gray-500 hover:text-gray-900'}`}>Financeiro</button>
               <button onClick={() => setViewMode('report')} className={`text-sm font-medium transition-colors ${viewMode === 'report' ? 'text-black' : 'text-gray-500 hover:text-gray-900'}`}>Relatório</button>
               <button onClick={() => setViewMode('export_json')} className={`text-sm font-medium transition-colors ${viewMode === 'export_json' ? 'text-black' : 'text-gray-500 hover:text-gray-900'}`}>Integrações</button>
               <button onClick={() => setViewMode('documents')} className={`text-sm font-medium transition-colors ${viewMode === 'documents' || viewMode === 'proposta_builder' ? 'text-black' : 'text-gray-500 hover:text-gray-900'}`}>Documentos</button>
               <button onClick={() => setViewMode('ia')} className={`text-sm font-medium transition-colors flex items-center gap-1 ${viewMode === 'ia' ? 'text-indigo-600 font-bold' : 'text-gray-500 hover:text-indigo-600'}`}>
                 <Brain size={16} /> IA
               </button>
               <button onClick={() => setViewMode('equipe')} className={`text-sm font-medium transition-colors flex items-center gap-1 ${viewMode === 'equipe' ? 'text-indigo-600 font-bold' : 'text-gray-500 hover:text-indigo-600'}`}>
                 <ShieldCheck size={16} /> Equipe
               </button>
            </nav>
            <div className="w-px h-4 bg-gray-200"></div>
            <button onClick={async () => {
              if (session) {
                await auth.signOut({});
                window.location.reload();
              } else {
                setIsAuthenticated(false);
              }
            }} className="text-sm text-gray-500 hover:text-black font-medium transition-colors">Sair</button>
          </div>
        </div>
      </header>
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/*
          A importacao em massa e uma TELA a parte, e nao um botao dentro de
          "Upload", de proposito: o upload existente le o ZIP na aba e publica
          dali mesmo — serve para punhado de site. Esta aqui entrega o trabalho
          ao worker e aguenta cinco mil. Misturar as duas num lugar so faria a
          pessoa escolher errado sem saber que escolheu.
        */}
        {viewMode === 'importacao_massa' && <ImportacaoEmMassa />}

        {/*
          CRIAR e IMPORTAR sao telas separadas porque sao operacoes diferentes, e
          a diferenca custa dinheiro: importar recebe site PRONTO e so publica;
          criar recebe LEAD (dados.json + fotos), cadastra o cliente e manda a IA
          escrever cada pagina — uma chamada paga por linha do lote. Um seletor
          dentro da mesma tela faria alguem escolher errado sem saber que
          escolheu, e o erro so apareceria na fatura.
        */}
        {viewMode === 'criacao_massa' && <CriacaoEmMassa />}

        {viewMode === 'upload' && (
          <div className="max-w-2xl mx-auto mt-12">
            <div className="text-center mb-8"><h2 className="text-2xl font-semibold text-gray-900 mb-2">Importar Projetos</h2><p className="text-gray-500 text-sm">Faça o upload do arquivo .zip contendo os sites dos clientes.</p></div>
            {pendingClients.length === 0 ? (
              <div className="bg-white border border-dashed border-gray-300 rounded-xl p-12 text-center hover:border-black transition-colors group">
                <div className="w-12 h-12 bg-gray-50 text-gray-400 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:text-black transition-colors">{isLoading ? <Loader2 className="animate-spin" /> : <UploadCloud size={24} />}</div>
                <h3 className="text-sm font-medium text-gray-900 mb-1">Selecione o arquivo ZIP</h3>
                <p className="text-xs text-gray-500 mb-6">Arraste e solte ou clique para buscar</p>
                <input type="file" accept=".zip" onChange={handleFileUpload} className="hidden" id="zip-upload" disabled={isLoading} />
                <label htmlFor="zip-upload" className={`inline-flex items-center justify-center px-4 py-2 border border-gray-200 text-sm font-medium rounded-md text-black bg-white hover:bg-gray-50 cursor-pointer transition-colors ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}>{isLoading ? 'Processando...' : 'Procurar arquivo'}</label>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="p-5 border-b border-gray-100 flex justify-between items-center">
                  <div><h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2"><CheckCircle className="text-black" size={16} />{pendingClients.length} Projetos Encontrados</h3></div>
                  <button onClick={approveAll} disabled={isLoading} className="bg-black hover:bg-gray-900 text-white px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2">{isLoading ? <Loader2 className="animate-spin" size={16} /> : null}Publicar Todos</button>
                </div>
                <ul className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
                  {pendingClients.map((client, idx) => (<li key={idx} className="p-4 hover:bg-gray-50 transition-colors flex items-center gap-4"><div className="w-8 h-8 bg-gray-100 text-gray-600 rounded-full flex items-center justify-center font-medium text-xs shrink-0">{client.data.name.charAt(0)}</div><div className="flex-1 min-w-0"><h4 className="text-sm font-medium text-gray-900 truncate">{client.data.name}</h4><p className="text-xs text-gray-500 truncate">{(client.data.categories || []).join(', ')}</p></div><span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] font-medium rounded uppercase">Pendente</span></li>))}
                </ul>
              </div>
            )}
          </div>
        )}
        {viewMode === 'report' && (
           <div className="flex flex-col items-center">
             <div className="w-full max-w-[210mm] flex justify-between items-center mb-6 no-print"><div className="text-sm text-slate-500">Mostrando {clients.length} clientes</div><button onClick={handlePrintReport} className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-indigo-700 shadow-md transition-all"><Download size={18} />Imprimir</button></div>
             <div id="report-container" className="bg-white w-full max-w-[210mm] min-h-[297mm] p-[15mm] shadow-xl border border-slate-200 text-slate-900">
                <div className="border-b-2 border-indigo-600 pb-4 mb-8 flex justify-between items-end"><div><h1 className="text-3xl font-bold text-slate-900">Relatório de Entrega</h1><p className="text-slate-500 mt-1 text-sm">Listagem oficial</p></div><div className="text-right"><div className="text-lg font-bold text-indigo-600">DithoSites</div><div className="text-xs text-slate-400">{new Date().toLocaleDateString('pt-BR')}</div></div></div>
                <div className="space-y-6">{clients.map((client, index) => { const siteLink = urlPublicaDoSite(client); return (<div key={client.id} className="break-inside-avoid border-b border-slate-100 pb-4 last:border-0"><div className="flex justify-between items-start mb-1"><h3 className="font-bold text-lg text-slate-800">{index + 1}. {client.data.name}</h3><span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded border border-slate-200 uppercase font-semibold">{(client.data.categories || [])[0] || 'Geral'}</span></div><p className="text-sm text-slate-600 mb-2 leading-snug">{client.data.summary.substring(0, 250)}...</p><div className="flex flex-col sm:flex-row gap-4 text-sm mt-2"><div className="flex-1"><span className="text-xs font-bold text-slate-400 uppercase block">Link</span><a href={siteLink} target="_blank" rel="noreferrer" className="text-indigo-600 underline break-all">{siteLink}</a></div><div className="sm:w-1/3"><span className="text-xs font-bold text-slate-400 uppercase block">Contato</span><span className="font-mono text-slate-700">{client.data.phoneNumber}</span></div></div></div>); })}</div>
                <div className="mt-12 pt-6 border-t border-slate-200 text-center text-xs text-slate-400">Gerado automaticamente pelo Ditho OS.</div>
             </div>
           </div>
        )}
        {viewMode === 'dashboard' && (
          <div className="space-y-6">
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap md:flex-nowrap items-center gap-3">
                <button onClick={toggleSelectAll} className="flex items-center gap-2 text-gray-500 hover:text-black font-medium px-2 py-2 rounded transition-colors">{selectedIds.size === filteredClients.length && filteredClients.length > 0 ? <CheckSquare size={18} className="text-black" /> : <Square size={18} />}</button>
                <div className="relative flex-1 min-w-[200px]"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} /><input type="text" placeholder="Buscar projetos..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-md text-sm outline-none focus:border-black focus:ring-1 focus:ring-black transition-all" /></div>
                <button onClick={() => setIsManualModalOpen(true)} className="flex items-center gap-2 px-3 py-2 rounded-md bg-black text-white font-medium text-sm hover:bg-gray-900 transition-colors shrink-0">+ Novo Cliente</button>
                {selectedIds.size > 0 && (<button onClick={() => handleDeleteRequest(Array.from(selectedIds))} className="flex items-center gap-2 px-3 py-2 rounded-md bg-white text-red-600 border border-gray-200 hover:border-red-200 hover:bg-red-50 font-medium text-sm transition-colors"><Trash2 size={16} />Excluir ({selectedIds.size})</button>)}
                {isGeneratingCovers ? (
                  <button onClick={() => { abortCoverGeneration.current = true; }} className="flex items-center gap-2 px-3 py-2 rounded-md border font-medium text-sm transition-all bg-red-50 text-red-600 border-red-200 hover:bg-red-100">
                    <X size={16} /><span className="hidden sm:inline">Parar Capas</span>
                  </button>
                ) : (
                  <button onClick={generateCovers} disabled={filteredClients.length === 0} className={`flex items-center gap-2 px-3 py-2 rounded-md border font-medium text-sm transition-all bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:text-black`}>
                    <Camera size={16} /><span className="hidden sm:inline">Gerar Capas</span>
                  </button>
                )}
                <button onClick={handleExport} disabled={isExporting || filteredClients.length === 0} className={`flex items-center gap-2 px-3 py-2 rounded-md border font-medium text-sm transition-all ${filteredClients.length > 0 ? 'bg-black border-black text-white hover:bg-gray-900' : 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'}`}>{isExporting ? <Loader2 size={16} className="animate-spin" /> : <Archive size={16} />}<span className="hidden sm:inline">Exportar</span></button>
                <div className="flex items-center bg-gray-100 p-0.5 rounded-md border border-gray-200 shrink-0"><button onClick={() => setLayoutView('grid')} className={`p-1.5 rounded-sm transition-all ${layoutView === 'grid' ? 'bg-white shadow-sm text-black' : 'text-gray-500 hover:text-gray-900'}`}><LayoutGrid size={16} /></button><button onClick={() => setLayoutView('list')} className={`p-1.5 rounded-sm transition-all ${layoutView === 'list' ? 'bg-white shadow-sm text-black' : 'text-gray-500 hover:text-gray-900'}`}><ListIcon size={16} /></button></div>
              </div>
              
              <div className="flex flex-wrap gap-3 items-center">
                {/* DATE FILTERS */}
                <div className="flex items-center gap-2">
                  <div className="flex bg-gray-100 p-0.5 rounded-md shrink-0">
                    {[
                      { id: 'all', label: 'Todos' }, 
                      { id: 'today', label: 'Hoje' }, 
                      { id: 'yesterday', label: 'Ontem' }, 
                      { id: 'week', label: '7 Dias' },
                      { id: 'custom', label: 'Personalizado' }
                    ].map((item) => (
                      <button 
                        key={item.id} 
                        onClick={() => setFilterDate(item.id as any)} 
                        className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-all whitespace-nowrap ${filterDate === item.id ? 'bg-white text-black shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                  {filterDate === 'custom' && (
                    <div className="flex items-center gap-2 animate-in fade-in slide-in-from-left-2 duration-300">
                      <input type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} className="bg-white border border-gray-200 text-gray-700 text-xs rounded-md px-2 py-1.5 outline-none focus:border-black" />
                      <span className="text-gray-400 text-xs font-medium">-</span>
                      <input type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} className="bg-white border border-gray-200 text-gray-700 text-xs rounded-md px-2 py-1.5 outline-none focus:border-black" />
                    </div>
                  )}
                </div>

                <div className="w-px h-4 bg-gray-200 hidden sm:block"></div>

                <div className="flex items-center gap-2">
                  <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="bg-white border border-gray-200 text-gray-700 text-xs rounded-md px-2 py-1.5 outline-none focus:border-black"><option value="all">Status: Todos</option>{allStatuses.map(s => <option key={s} value={s}>{s}</option>)}</select>
                  <select value={filterMinRating} onChange={(e) => setFilterMinRating(Number(e.target.value))} className="bg-white border border-gray-200 text-gray-700 text-xs rounded-md px-2 py-1.5 outline-none focus:border-black"><option value="0">Nota: Todas</option><option value="4.5">4.5+</option><option value="4">4.0+</option><option value="3">3.0+</option></select>
                  <select value={filterNiche} onChange={(e) => setFilterNiche(e.target.value)} className="bg-white border border-gray-200 text-gray-700 text-xs rounded-md px-2 py-1.5 outline-none focus:border-black max-w-[150px]"><option value="all">Nicho: Todos</option>{allCategories.map(c => <option key={c} value={c}>{c}</option>)}</select>
                  {(filterNiche !== 'all' || filterStatus !== 'all' || filterMinRating > 0 || filterDate !== 'today') && (<button onClick={() => { setFilterNiche('all'); setFilterStatus('all'); setFilterMinRating(0); setFilterDate('today'); setCustomStartDate(''); setCustomEndDate(''); }} className="text-xs text-gray-500 hover:text-black font-medium px-2 transition-colors">Limpar filtros</button>)}
                </div>
              </div>
            </div>

            {erroListagem && (
              <div className="mb-4 flex items-start gap-3 bg-red-50 border border-red-200 text-red-800 rounded-xl p-4">
                <AlertCircle size={18} className="shrink-0 mt-0.5" />
                <div className="text-sm">
                  <b>A lista não carregou.</b> O que está na tela pode estar desatualizado —
                  isto é uma falha ao falar com a API, não "nenhum cliente".
                  <p className="font-mono text-xs mt-1 break-all">{erroListagem}</p>
                  <button
                    onClick={() => void loadClients()}
                    className="mt-2 px-3 py-1.5 text-xs font-medium text-white bg-red-700 hover:bg-red-800 rounded-md transition-colors"
                  >
                    Tentar de novo
                  </button>
                </div>
              </div>
            )}
            {filteredClients.length === 0 ? (<div className="text-center py-20 bg-white rounded-xl border border-gray-200"><div className="w-12 h-12 bg-gray-50 text-gray-300 rounded-full flex items-center justify-center mx-auto mb-3"><Search size={24} /></div><h3 className="text-sm font-medium text-gray-900">{erroListagem ? 'A lista não carregou' : 'Nenhum projeto encontrado'}</h3><p className="text-xs text-gray-500 mt-1">{erroListagem ? 'A API não respondeu — veja o erro acima.' : 'A API respondeu, e nenhum cliente bate com os filtros atuais.'}</p></div>) : (layoutView === 'grid' ? (<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">{filteredClients.map((client) => { const hasCover = coverImages[client.id]; const isSelected = selectedIds.has(client.id); const isRegenerating = regeneratingId === client.id; return (<div key={client.id} onClick={() => handleClientClick(client)} className={`group bg-white rounded-xl border transition-all cursor-pointer overflow-hidden flex flex-col h-full relative ${isSelected ? 'border-black ring-1 ring-black' : 'border-gray-200 hover:border-gray-300'}`}><div onClick={(e) => { e.stopPropagation(); toggleSelection(client.id); }} className={`absolute top-2 left-2 z-20 p-1 rounded-md transition-colors ${isSelected ? 'bg-black text-white' : 'bg-white/90 text-gray-400 hover:text-black'}`}>{isSelected ? <CheckSquare size={16} /> : <Square size={16} />}</div><div onClick={(e) => regenerateSingleCover(client, e)} className="absolute top-2 right-9 z-20 p-1 rounded-md bg-white/90 text-gray-500 hover:text-black shadow-sm">{isRegenerating ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}</div><div onClick={(e) => { e.stopPropagation(); handleDeleteRequest([client.id]); }} className="absolute top-2 right-2 z-20 p-1 rounded-md bg-white/90 text-gray-400 hover:text-red-600 shadow-sm"><Trash2 size={16} /></div><div className={`h-36 relative overflow-hidden border-b border-gray-100 ${!hasCover ? 'bg-gray-50' : 'bg-gray-100'}`}>{hasCover ? <img src={hasCover} alt="Preview" className="w-full h-full object-cover object-top" /> : <div className="absolute inset-0 flex items-center justify-center text-gray-300"><LayoutTemplate size={32} /></div>}</div><div className="p-4 flex-1 flex flex-col"><div className="flex justify-between items-start mb-1.5"><span className="text-[10px] font-medium text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded uppercase tracking-wider">{client.data.businessStatus}</span><div className="flex items-center gap-1 text-gray-400 text-xs"><Star size={10} className="text-gray-400 fill-current" />{client.data.rating}</div></div><h3 className="text-sm font-semibold text-gray-900 mb-1 leading-tight group-hover:text-black transition-colors">{client.data.name}</h3><p className="text-xs text-gray-500 line-clamp-2 mb-3 flex-1">{client.data.summary}</p><div className="flex flex-wrap gap-1 mt-auto">{(client.data.categories || []).slice(0, 2).map((cat, i) => (<span key={i} className="text-[10px] text-gray-500 px-0">{cat}{i === 0 && (client.data.categories || []).length > 1 ? ',' : ''}</span>))}</div></div></div>); })}</div>) : (<div className="bg-white rounded-xl border border-gray-200 overflow-hidden"><div className="overflow-x-auto"><table className="w-full text-left text-sm text-gray-600"><thead className="bg-gray-50 border-b border-gray-200 font-medium text-gray-500 text-xs"><tr><th className="px-4 py-3 w-10"><div onClick={toggleSelectAll} className="cursor-pointer text-gray-400 hover:text-black">{selectedIds.size === filteredClients.length && filteredClients.length > 0 ? <CheckSquare size={16} className="text-black" /> : <Square size={16} />}</div></th><th className="px-4 py-3 font-medium">Projeto</th><th className="px-4 py-3 font-medium">Data</th><th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3 text-right font-medium">Ação</th></tr></thead><tbody className="divide-y divide-gray-100">{filteredClients.map((client) => { const isSelected = selectedIds.has(client.id); return (<tr key={client.id} onClick={() => handleClientClick(client)} className={`cursor-pointer transition-colors group ${isSelected ? 'bg-gray-50' : 'hover:bg-gray-50'}`}><td className="px-4 py-3"><div onClick={(e) => { e.stopPropagation(); toggleSelection(client.id); }} className="cursor-pointer text-gray-400 hover:text-black">{isSelected ? <CheckSquare size={16} className="text-black" /> : <Square size={16} />}</div></td><td className="px-4 py-3"><div className="text-sm font-medium text-gray-900">{client.data.name}</div></td><td className="px-4 py-3 text-xs text-gray-500">{new Date(client.data.createdAt).toLocaleDateString()}</td><td className="px-4 py-3"><span className="text-[10px] font-medium text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded uppercase tracking-wider">{client.data.businessStatus}</span></td><td className="px-4 py-3 text-right"><div className="flex items-center justify-end gap-2"><button className="text-gray-500 hover:text-black font-medium text-xs px-2 py-1 transition-colors">Detalhes</button><button onClick={(e) => { e.stopPropagation(); handleDeleteRequest([client.id]); }} className="p-1 text-gray-400 hover:text-red-600 rounded transition-colors"><Trash2 size={14} /></button></div></td></tr>); })}</tbody></table></div></div>))}
          </div>
        )}
        {viewMode === 'finance' && (
          <AdminFinanceBoard clients={clients} onClientUpdate={loadClients} />
        )}
        {viewMode === 'export_json' && (
          <IntegrationsDashboard 
            totalClientsCount={totalClientsCount}
            clientsLength={clients.length}
            isExportingJson={isExportingJson}
            onExportJson={handleExportJson}
          />
        )}
        {viewMode === 'documents' && (
          <DocumentsDashboard onOpenDocument={(docId) => {
             if (docId === 'proposta_assinatura_1000') setViewMode('proposta_builder');
          }} />
        )}
        {viewMode === 'proposta_builder' && (
          <PropostaBuilder />
        )}
        {viewMode === 'ia' && (
          <IaControlView />
        )}
        {viewMode === 'equipe' && (
          <TeamManager />
        )}
      </main>
      {selectedClient && ( <ClientModal client={selectedClient} coverImage={coverImages[selectedClient.id]} onClose={() => setSelectedClient(null)} onPreview={(client) => { setSelectedClient(null); setPreviewClient(client); }} onVisualEdit={(client) => { setSelectedClient(null); setEditingClient(client); }} onClientUpdate={loadClients} /> )}
      {previewClient && ( <SitePreview client={previewClient} onClose={() => setPreviewClient(null)} /> )}
      {editingClient && ( <SiteEditor clientEntity={editingClient} onClose={() => setEditingClient(null)} /> )}
      {isManualModalOpen && ( <ManualClientModal onClose={() => setIsManualModalOpen(false)} onSave={() => { setIsManualModalOpen(false); loadClients(); }} /> )}
    </div>
    );
  }

  // Public Site Viewer (No Auth Required)
  if (publicSlug && !isEditMode) return <PublicSiteViewer slug={publicSlug} />;

  // Protected Admin Route: Full Screen Editor
  if (publicSlug && isEditMode) return <SiteEditor slug={publicSlug} />;

  if (propostaSlug) return <Proposta slug={propostaSlug} />;

  // Fallback to Landing Page Route (Default for empty hash)
  return <NyrohLanding />;

}

export default App;
